import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Upload, CheckCircle, XCircle, AlertCircle, Database, BookOpen, Download, PlayCircle, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ImportProgress {
  total: number;
  processed: number;
  imported: number;
  updated: number;
  skipped: number;
  errors: number;
  currentBatch: number;
  totalBatches: number;
  status: 'processing' | 'complete' | 'error';
  errorDetails?: Array<{ book: string; error: string }>;
}

export default function AdminBookImport() {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [fetching, setFetching] = useState(false);
  const [bookCount, setBookCount] = useState(100);
  const [importStats, setImportStats] = useState<{ 
    total_fetched: number; 
    next_offset: number;
    current_strategy?: string;
    current_subject?: string;
    strategy_offset?: number;
  } | null>(null);
  const [backgroundJobs, setBackgroundJobs] = useState<any[]>([]);
  const [startingBackground, setStartingBackground] = useState(false);
  const [preferISBNdb, setPreferISBNdb] = useState(true);

  // Fetch import stats and background jobs on mount
  useEffect(() => {
    const fetchData = async () => {
      const { data } = await supabase
        .from('openlibrary_import_progress')
        .select('total_fetched, last_offset, current_strategy, current_subject, strategy_offset')
        .limit(1)
        .single();
      
      if (data) {
        setImportStats({ 
          total_fetched: data.total_fetched, 
          next_offset: data.last_offset,
          current_strategy: data.current_strategy,
          current_subject: data.current_subject,
          strategy_offset: data.strategy_offset
        });
      }

      // Fetch background jobs
      const { data: jobs } = await supabase
        .from('background_import_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (jobs) {
        setBackgroundJobs(jobs);
      }
    };
    fetchData();

    // Poll for background job updates every 5 seconds
    const interval = setInterval(async () => {
      const { data: jobs } = await supabase
        .from('background_import_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (jobs) {
        setBackgroundJobs(jobs);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Redirect if not admin
  if (!adminLoading && !isAdmin) {
    navigate('/');
    return null;
  }

  if (adminLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-48 bg-muted rounded" />
        </div>
      </div>
    );
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast({
          variant: 'destructive',
          title: 'Invalid file',
          description: 'Please select a JSON file',
        });
        return;
      }
      setSelectedFile(file);
      setProgress(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);
    setProgress(null);

    try {
      // Read file
      const fileText = await selectedFile.text();
      const books = JSON.parse(fileText);

      if (!Array.isArray(books)) {
        throw new Error('File must contain an array of books');
      }

      toast({
        title: 'Import started',
        description: `Processing ${books.length} books...`,
      });

      // Call edge function
      const { data, error } = await supabase.functions.invoke('bulk-import-books', {
        body: { books },
      });

      if (error) {
        throw error;
      }

      setProgress(data as ImportProgress);

      if (data.status === 'complete') {
        toast({
          title: 'Import complete',
          description: `✅ ${data.imported} imported, ⏭️ ${data.skipped} updated, ❌ ${data.errors} errors`,
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        variant: 'destructive',
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      setProgress({
        total: 0,
        processed: 0,
        imported: 0,
        updated: 0,
        skipped: 0,
        errors: 0,
        currentBatch: 0,
        totalBatches: 0,
        status: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleFetchAndImport = async () => {
    setFetching(true);
    setProgress(null);

    try {
      toast({
        title: 'Fetching books',
        description: `Fetching ${bookCount} books from Open Library...`,
      });

      // Call fetch edge function
      const { data: fetchData, error: fetchError } = await supabase.functions.invoke(
        'fetch-openlibrary-books',
        {
          body: { count: bookCount },
        }
      );

      if (fetchError) {
        throw fetchError;
      }

      const books = fetchData.books || [];
      const hasMore = fetchData.hasMore || false;
      const totalFetched = fetchData.total_fetched || 0;
      const nextOffset = fetchData.next_offset || 0;
      
      // Update local import stats
      setImportStats({
        total_fetched: totalFetched,
        next_offset: nextOffset,
      });

      // Handle case where no books were fetched
      if (books.length === 0) {
        if (totalFetched > 0) {
          // This is fine - we've reached the end
          toast({
            title: 'No more books available',
            description: `Total fetched so far: ${totalFetched}. ${hasMore ? 'Try again later.' : 'You\'ve reached the end of available books.'}`,
          });
        } else {
          // First attempt returned nothing - might be an issue
          toast({
            variant: 'destructive',
            title: 'No books fetched',
            description: 'Open Library returned no results. Check edge function logs for details.',
          });
        }
        return;
      }

      toast({
        title: 'Books fetched',
        description: `✅ ${books.length} books fetched${hasMore ? ' (more available)' : ''}. Starting import...`,
      });

      // Now import the fetched books
      const { data: importData, error: importError } = await supabase.functions.invoke(
        'bulk-import-books',
        {
          body: { books },
        }
      );

      if (importError) {
        throw importError;
      }

      setProgress(importData as ImportProgress);

      if (importData.status === 'complete') {
        toast({
          title: 'Import complete',
          description: `✅ ${importData.imported} new, 🔄 ${importData.updated} updated, ⏭️ ${importData.skipped} skipped, ❌ ${importData.errors} errors`,
        });
      }
    } catch (error) {
      console.error('Fetch and import error:', error);
      setProgress({
        total: 0,
        processed: 0,
        imported: 0,
        updated: 0,
        skipped: 0,
        errors: 1,
        currentBatch: 0,
        totalBatches: 0,
        status: 'error',
        errorDetails: [{ book: 'Fetch operation', error: error.message }],
      });
      toast({
        variant: 'destructive',
        title: 'Operation failed',
        description: error.message,
      });
    } finally {
      setFetching(false);
    }
  };

  const handleStartBackgroundImport = async () => {
    setStartingBackground(true);
    try {
      toast({
        title: 'Starting background import',
        description: `Starting background import of ${bookCount} books...`,
      });

      const { data, error } = await supabase.functions.invoke('start-background-import', {
        body: { 
          count: bookCount,
          preferISBNdb: preferISBNdb 
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Background import started',
        description: 'Import is running in the background. You can close this tab.',
      });

      // Refresh jobs list
      const { data: jobs } = await supabase
        .from('background_import_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (jobs) {
        setBackgroundJobs(jobs);
      }
    } catch (error) {
      console.error('Background import error:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to start background import',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setStartingBackground(false);
    }
  };

  const handleResetProgress = async () => {
    if (!confirm('Reset import progress? This will start fetching from the beginning on the next import.')) {
      return;
    }

    try {
      const { error } = await supabase.functions.invoke('reset-openlibrary-progress');
      
      if (error) {
        throw error;
      }

      // Refresh stats
      const { data } = await supabase
        .from('openlibrary_import_progress')
        .select('total_fetched, last_offset')
        .limit(1)
        .single();
      
      if (data) {
        setImportStats({ total_fetched: data.total_fetched, next_offset: data.last_offset });
      }

      toast({
        title: 'Progress reset',
        description: 'Import progress has been reset. Next fetch will start from the beginning.',
      });
    } catch (error) {
      console.error('Reset error:', error);
      toast({
        variant: 'destructive',
        title: 'Reset failed',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const progressPercentage = progress
    ? Math.round((progress.processed / progress.total) * 100)
    : 0;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Bulk Book Import</h1>
        <p className="text-muted-foreground">
          Import thousands of popular books from OpenLibrary data
        </p>
      </div>

      <div className="space-y-6">
        {/* Reset Progress Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Reset Import Progress
            </CardTitle>
            <CardDescription>
              Clear the import progress to start fresh with new queries. This will force the system to begin from the start with the updated thriller/crime-focused queries.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleResetProgress}
              disabled={fetching || importing || startingBackground}
              variant="destructive"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Reset Progress & Start Fresh
            </Button>
          </CardContent>
        </Card>

        {/* Background Import Status */}
        {backgroundJobs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PlayCircle className="h-5 w-5" />
                Background Import Jobs
              </CardTitle>
              <CardDescription>
                Recent background import jobs (auto-refreshes every 5 seconds)
              </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                {backgroundJobs.map((job) => (
                  <div key={job.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {job.status === 'running' && (
                          <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
                        )}
                        {job.status === 'completed' && (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        )}
                        {job.status === 'failed' && (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        {job.status === 'cancelled' && (
                          <XCircle className="h-4 w-4 text-orange-500" />
                        )}
                        {job.status === 'pending' && (
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                        )}
                        <span className="font-medium capitalize">{job.status}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {job.status === 'running' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                await supabase
                                  .from('background_import_jobs')
                                  .update({ status: 'cancelled' })
                                  .eq('id', job.id);
                                
                                // Immediately refetch jobs
                                const { data: jobs } = await supabase
                                  .from('background_import_jobs')
                                  .select('*')
                                  .order('created_at', { ascending: false })
                                  .limit(5);
                                
                                if (jobs) {
                                  setBackgroundJobs(jobs);
                                }
                                
                                toast({ title: 'Job cancellation requested' });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const { error } = await supabase.functions.invoke('force-cancel-import', {
                                    body: { jobId: job.id }
                                  });
                                  
                                  if (error) throw error;
                                  
                                  // Immediately refetch jobs
                                  const { data: jobs } = await supabase
                                    .from('background_import_jobs')
                                    .select('*')
                                    .order('created_at', { ascending: false })
                                    .limit(5);
                                  
                                  if (jobs) {
                                    setBackgroundJobs(jobs);
                                  }
                                  
                                  toast({ title: 'Job force cancelled' });
                                } catch (error) {
                                  toast({ 
                                    variant: 'destructive',
                                    title: 'Failed to force cancel',
                                    description: error instanceof Error ? error.message : 'Unknown error'
                                  });
                                }
                              }}
                            >
                              Force Cancel
                            </Button>
                          </>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {new Date(job.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Requested:</span>{' '}
                        <strong>{job.requested_count}</strong>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Fetched:</span>{' '}
                        <strong>{job.books_fetched || 0}</strong>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Imported:</span>{' '}
                        <strong className="text-green-600">{job.books_imported || 0}</strong>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Updated:</span>{' '}
                        <strong className="text-blue-600">{job.books_updated || 0}</strong>
                      </div>
                    </div>
                    
                    {/* Fetch Progress Indicator */}
                    {job.status === 'running' && job.fetch_progress_target > 0 && (
                      <div className="mt-4 p-3 bg-accent/50 rounded-md space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">
                            {job.fetch_progress_current < job.fetch_progress_target 
                              ? '📥 Fetching books from API...'
                              : '💾 Importing to database...'}
                          </span>
                          <span className="text-muted-foreground">
                            {job.fetch_progress_current}/{job.fetch_progress_target} books
                          </span>
                        </div>
                        <Progress 
                          value={(job.fetch_progress_current / job.fetch_progress_target) * 100} 
                          className="h-2"
                        />
                        <div className="text-xs text-muted-foreground">
                          {Math.round((job.fetch_progress_current / job.fetch_progress_target) * 100)}% complete
                        </div>
                      </div>
                    )}
                    
                    {job.status === 'running' && job.books_fetched > 0 && job.started_at && (
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Est. time remaining:</span>
                          <span className="font-medium text-amber-600">
                            {(() => {
                              const elapsed = new Date().getTime() - new Date(job.started_at).getTime();
                              const rate = job.books_fetched / (elapsed / 1000); // books per second
                              const remaining = job.requested_count - job.books_fetched;
                              const secondsRemaining = remaining / rate;
                              const minutes = Math.floor(secondsRemaining / 60);
                              const seconds = Math.floor(secondsRemaining % 60);
                              return `~${minutes}m ${seconds}s`;
                            })()}
                          </span>
                        </div>
                      </div>
                    )}
                    {job.error_message && (
                      <div className="mt-2 text-sm text-red-600">
                        Error: {job.error_message}
                      </div>
                    )}
                    {job.status === 'running' && (
                      <div className="mt-2">
                        <Progress 
                          value={(job.books_fetched / job.requested_count) * 100} 
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {Math.round((job.books_fetched / job.requested_count) * 100)}% complete
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Background Import Launcher */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlayCircle className="h-5 w-5" />
              Background Import (Recommended)
            </CardTitle>
            <CardDescription>
              Start an import that runs in the background. You can close this tab and it will continue.
              Uses a multi-strategy approach (rating → want_to_read → subjects → editions) to import high-quality, searchable books.
              {importStats && (
                <div className="mt-2 space-y-1">
                  <div className="text-sm font-medium">📚 Total books fetched: <strong>{importStats.total_fetched.toLocaleString()}</strong></div>
                  {importStats.current_strategy && (
                    <div className="text-sm">
                      🎯 Current strategy: <strong className="capitalize">{importStats.current_strategy}</strong>
                      {importStats.current_subject && ` (${importStats.current_subject})`}
                      {importStats.strategy_offset !== undefined && ` • Offset: ${importStats.strategy_offset.toLocaleString()}`}
                    </div>
                  )}
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/50">
              <div className="space-y-0.5">
                <Label htmlFor="isbndb-toggle" className="text-sm font-medium">
                  Prefer ISBNdb API
                </Label>
                <p className="text-xs text-muted-foreground">
                  Use ISBNdb for higher quality books (requires API key). Falls back to OpenLibrary.
                </p>
              </div>
              <Switch
                id="isbndb-toggle"
                checked={preferISBNdb}
                onCheckedChange={setPreferISBNdb}
                disabled={startingBackground}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="bgBookCount" className="text-sm font-medium">
                Number of books to import
              </label>
              <Input
                id="bgBookCount"
                type="number"
                min="1"
                max="10000"
                value={bookCount}
                onChange={(e) => setBookCount(parseInt(e.target.value) || 100)}
                disabled={startingBackground}
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground">
                Between 1 and 10,000 books. Import runs in the background - you can close this tab.
              </p>
            </div>

            <Button
              onClick={handleStartBackgroundImport}
              disabled={startingBackground || bookCount < 1 || bookCount > 10000}
              className="w-full"
            >
              {startingBackground ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Start Background Import
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Fetch from Open Library */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Fetch Books from Open Library
            </CardTitle>
            <CardDescription>
              Automatically fetch popular books from Open Library and import them directly
              {importStats && (
                <div className="mt-2 space-y-1">
                  <div className="text-sm font-medium">📚 Total books fetched: <strong>{importStats.total_fetched.toLocaleString()}</strong></div>
                  <div className="text-xs text-muted-foreground">Next batch will continue from book #{(importStats.next_offset + 1).toLocaleString()}</div>
                </div>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="bookCount" className="text-sm font-medium">
                Number of books to fetch
              </label>
              <Input
                id="bookCount"
                type="number"
                min="1"
                max="10000"
                value={bookCount}
                onChange={(e) => setBookCount(parseInt(e.target.value) || 100)}
                disabled={fetching || importing}
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground">
                Between 1 and 10,000 books (recommended: start with 100)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleFetchAndImport}
                disabled={fetching || importing || bookCount < 1 || bookCount > 10000}
                className="flex-1"
              >
                {fetching ? (
                  <>
                    <Database className="mr-2 h-4 w-4 animate-spin" />
                    Fetching & Importing...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Fetch & Import Books
                  </>
                )}
              </Button>
              <Button
                onClick={handleResetProgress}
                variant="outline"
                disabled={fetching || importing}
              >
                Reset Progress
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* File Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload JSON File
            </CardTitle>
            <CardDescription>
              Or upload a JSON file containing book data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose File
              </Button>
              {selectedFile && (
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>{selectedFile.name}</span>
                  <span className="text-muted-foreground">
                    ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              )}
            </div>

            {selectedFile && (
              <Button
                onClick={handleImport}
                disabled={importing}
                size="lg"
                className="w-full"
              >
                <Database className="h-4 w-4 mr-2" />
                {importing ? 'Importing...' : 'Start Import'}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Progress Card */}
        {progress && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {progress.status === 'complete' && (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Import Complete
                  </>
                )}
                {progress.status === 'processing' && (
                  <>
                    <AlertCircle className="h-5 w-5 text-blue-600 animate-pulse" />
                    Importing...
                  </>
                )}
                {progress.status === 'error' && (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    Import Failed
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span className="font-medium">
                    {progress.processed} / {progress.total} books
                  </span>
                </div>
                <Progress value={progressPercentage} className="h-2" />
                <div className="text-xs text-muted-foreground text-center">
                  Batch {progress.currentBatch} of {progress.totalBatches}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {progress.imported}
                  </div>
                  <div className="text-xs text-muted-foreground">New</div>
                </div>
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {progress.updated}
                  </div>
                  <div className="text-xs text-muted-foreground">Updated</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {progress.skipped}
                  </div>
                  <div className="text-xs text-muted-foreground">Skipped</div>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {progress.errors}
                  </div>
                  <div className="text-xs text-muted-foreground">Errors</div>
                </div>
              </div>

              {progress.errorDetails && progress.errorDetails.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <div className="text-sm font-medium mb-2">Errors encountered:</div>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {progress.errorDetails.map((err, idx) => (
                        <div key={idx} className="text-xs">
                          <span className="font-mono">{err.book}:</span> {err.error}
                        </div>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
